import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth, effectiveTier } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import CreditCostStrip from "@/components/credits/CreditCostStrip";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { Loader2, Download, Mic, Lock } from "lucide-react";

const VOICES = [
  { id: "onyx",    label: "Onyx · Deep male" },
  { id: "echo",    label: "Echo · Warm male" },
  { id: "ash",     label: "Ash · Crisp male" },
  { id: "fable",   label: "Fable · British" },
  { id: "alloy",   label: "Alloy · Neutral" },
  { id: "sage",    label: "Sage · Calm" },
  { id: "nova",    label: "Nova · Bright female" },
  { id: "shimmer", label: "Shimmer · Soft female" },
  { id: "coral",   label: "Coral · Conversational" },
];

const MODELS = [
  { id: "tts-1-hd", label: "HD · Best quality" },
  { id: "tts-1",    label: "Standard · Faster" },
];

export default function VoiceoverGeneratePage() {
  const { user, loading, refresh } = useAuth();
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("onyx");
  const [model, setModel] = useState("tts-1-hd");
  const [speed, setSpeed] = useState(1.0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/generate/voiceover" replace />;

  const tier = effectiveTier(user);
  const locked = !tier; // free-trial users (incl. owners impersonating free) can't use voiceover

  const onGenerate = async () => {
    setError(""); setResult(null);
    if (text.trim().length < 1) { setError("Type the script you want voiced."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/generate/voiceover", { text, voice, model, speed });
      setResult(data);
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadHref = result ? `${BACKEND_URL}${result.download_url}` : null;
  const previewHref = result ? `${BACKEND_URL}${result.preview_url}` : null;

  return (
    <DashboardShell>
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-4">Voiceover Generator</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Studio-grade voiceovers</h1>
        <p className="text-[#A0A0A0] mt-4 max-w-2xl">
          9 cinematic voices, HD quality, English text — perfect for commercials, promos and social ads. Every voiceover debits credits from your wallet (≈ 4 credits per 60s on standard tier).
        </p>
      </div>

      {locked && (
        <div
          data-testid="voiceover-locked-banner"
          className="mb-10 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5"
        >
          <Lock className="w-7 h-7 text-[#D4AF37] flex-shrink-0" />
          <div className="flex-1">
            <p className="font-display text-xl font-bold text-[#F4F4F0] mb-1">Voiceover is a paid feature</p>
            <p className="text-sm text-[#A0A0A0]">Unlock studio TTS by picking any plan — Weekly, Starter, Pro or Premium.</p>
          </div>
          <Link
            to="/pricing"
            data-testid="voiceover-upgrade-cta"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#080808] font-bold px-5 py-3 text-sm hover:bg-[#F0C84B] transition-colors whitespace-nowrap"
          >
            See Plans
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Controls */}
        <div className="lg:col-span-2 border border-[#2A2A2A] bg-[#121212] p-6 md:p-8">
          <label className="block mb-6">
            <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#A0A0A0] mb-2">Script</span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={7}
              placeholder="At YBM Studios, every frame is engineered to convert. Premium ads, on demand."
              data-testid="voiceover-text-input"
              disabled={locked}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-3 text-[#F4F4F0] placeholder-[#5A5A5A] transition-colors resize-y disabled:opacity-50"
            />
            <p className="text-[10px] text-[#5A5A5A] mt-2 uppercase tracking-wider">{text.length} / 4000 characters</p>
          </label>

          <Selector cols={3} label="Voice" value={voice} options={VOICES} onChange={setVoice} testId="voiceover-voice" disabled={locked} />
          <Selector cols={2} label="Model" value={model} options={MODELS} onChange={setModel} testId="voiceover-model" disabled={locked} />

          <div className="mb-6">
            <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#A0A0A0] mb-2">
              Speed · {speed.toFixed(2)}×
            </span>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.05"
              value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              data-testid="voiceover-speed"
              disabled={locked}
              className="w-full accent-[#D4AF37] disabled:opacity-50"
            />
          </div>

          {!locked && <CreditCostStrip feature="voice" quality="pro" duration={60} label="≈ 60s · Pro" />}

          {error && <div className="mb-5 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="voiceover-error">{error}</div>}

          <button
            onClick={onGenerate}
            disabled={busy || locked}
            data-testid="voiceover-generate-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#080808] font-bold py-3.5 hover:bg-[#F0C84B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Synthesising…</>) : (<><Mic className="w-4 h-4" /> Generate Voiceover</>)}
          </button>

          {!locked && (
            <p className="text-xs text-[#A0A0A0] mt-4 text-center">
              Free to generate · downloading the MP3 counts as 1 toward your plan.
            </p>
          )}
        </div>

        {/* Preview */}
        <div className="lg:col-span-3 border border-[#2A2A2A] bg-[#0A0A0A] min-h-[420px] flex items-center justify-center p-6">
          {busy && (
            <div className="text-center" data-testid="voiceover-loading-state">
              <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
              <p className="text-sm text-[#A0A0A0]">Synthesising voiceover…</p>
              <p className="text-xs text-[#5A5A5A] mt-1">Usually 5–20 seconds depending on length.</p>
            </div>
          )}
          {!busy && result && (
            <div className="w-full" data-testid="voiceover-result">
              <div className="bg-black p-10 flex flex-col items-center gap-6">
                <div className="w-24 h-24 rounded-full border-4 border-[#D4AF37] flex items-center justify-center">
                  <Mic className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <audio
                  src={previewHref}
                  controls
                  data-testid="voiceover-audio-player"
                  className="w-full max-w-md"
                />
                <p className="text-xs text-[#A0A0A0] uppercase tracking-[0.22em]">
                  {result.voice} · {result.model}
                </p>
              </div>
              <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
                <span className="text-xs text-[#A0A0A0]">No watermark · {result.tier_at_render}</span>
                <a
                  href={downloadHref}
                  data-testid="voiceover-download-button"
                  className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-5 py-2.5 text-sm transition-colors"
                >
                  <Download className="w-4 h-4" /> Download MP3
                </a>
              </div>
            </div>
          )}
          {!busy && !result && (
            <div className="text-center text-[#5A5A5A] text-sm">Your generated voiceover will appear here.</div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function Selector({ label, value, options, onChange, testId, cols = 2, disabled }) {
  const colsClass = cols === 2 ? "grid-cols-2" : cols === 3 ? "grid-cols-3" : "grid-cols-4";
  return (
    <div className="mb-6">
      <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#A0A0A0] mb-2">{label}</span>
      <div className={`grid ${colsClass} gap-2`}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            disabled={disabled}
            data-testid={`${testId}-${o.id}`}
            className={`px-3 py-2.5 text-xs md:text-sm border transition-colors disabled:opacity-50 ${
              value === o.id
                ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                : "border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F4F4F0]"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
